"use client";
import { useEffect, useRef, useState } from "react";
import "./ScrollStacking.css";

let gsap, ScrollTrigger;

const ScrollStacking = ({
  children,
  direction = "vertical",
  className = "",
  sectionDirections = {},
}) => {
  const sectionRef = useRef(null);
  const wrapperRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const loadGSAP = async () => {
      if (typeof window === "undefined") return;

      try {
        const gsapModule = await import("gsap");
        const ScrollTriggerModule = await import("gsap/ScrollTrigger");

        gsap = gsapModule.gsap;
        ScrollTrigger = ScrollTriggerModule.ScrollTrigger;

        gsap.registerPlugin(ScrollTrigger);
        setIsLoaded(true);
      } catch (error) {
        console.error("Failed to load GSAP:", error);
      }
    };

    loadGSAP();
  }, []);

  useEffect(() => {
    if (!isLoaded || typeof window === "undefined" || !gsap || !ScrollTrigger) return;

    const section = sectionRef.current;
    const wrapper = wrapperRef.current;
    const items = wrapper.querySelectorAll(".stacking-item");

    if (!section || !wrapper || items.length === 0) return;

    // Set initial states
    items.forEach((item, index) => {
      if (index !== 0) {
        const itemId = item.id;
        const itemDirection = sectionDirections[itemId] || direction;

        if (itemDirection === "horizontal") {
          gsap.set(item, { xPercent: 100, opacity: 1 });
        } else {
          gsap.set(item, { yPercent: 100, opacity: 1 });
        }
      }
    });

    // Create timeline
    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        pin: true,
        start: "top top",
        end: () => `+=${items.length * 150}%`,
        scrub: 1.5,
        invalidateOnRefresh: true,
        // markers: true,
      },
      defaults: { ease: "power2.inOut" },
    });

    // Animate each item with sequential timing
    items.forEach((item, index) => {
      if (index < items.length - 1) {
        const nextItem = items[index + 1];
        const nextItemId = nextItem.id;
        const nextItemDirection = sectionDirections[nextItemId] || direction;

        timeline.to(item, {
          scale: 0.9,
          borderRadius: "10px",
          duration: 1,
        });

        // Slide in next item - start after current item animation is 90% complete
        if (nextItemDirection === "horizontal") {
          timeline.to(
            nextItem,
            {
              xPercent: 0,
              duration: 1,
            },
            "-=0.1"
          );
        } else {
          timeline.to(
            nextItem,
            {
              yPercent: 0,
              duration: 1,
            },
            "-=0.1"
          );
        }

        // Add a pause between sections to ensure full completion
        timeline.to({}, { duration: 0.8 });
      }
    });

    // Cleanup function
    return () => {
      if (ScrollTrigger) {
        ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      }
    };
  }, [direction, sectionDirections, isLoaded]);

  return (
    <div
      ref={sectionRef}
      className={`scroll-section ${direction}-section ${className}`}
    >
      <div ref={wrapperRef} className="stacking-wrapper">
        {children}
      </div>
    </div>
  );
};

export default ScrollStacking;
